/**
 * CustomerLookupAutocomplete — debounced search across phone + full name.
 *
 * Behaviour:
 *   - 300 ms debounce on the search term.
 *   - Empty term → no query, no dropdown.
 *   - Up to 10 results; each result shows name, masked phone, customer #.
 *   - Final option in the dropdown is "Create new walk-in" — selecting it
 *     calls `onCreateNew()` so the parent can switch to inline-creation.
 *   - The component is uncontrolled w.r.t. selection; the parent decides
 *     what to do with the picked customer via `onSelect`.
 */
import { type JSX, useEffect, useRef, useState } from 'react';

import { lookupCustomers } from '../../lib/api/customers';
import type { CustomerResponse } from '../../lib/api/types';

const DEBOUNCE_MS = 300;
const MAX_RESULTS = 10;

interface CustomerLookupAutocompleteProps {
  onSelect: (customer: CustomerResponse) => void;
  onCreateNew: () => void;
  placeholder?: string;
}

function maskPhone(phone: string): string {
  if (phone.length <= 4) {
    return phone;
  }
  const tail = phone.slice(-3);
  return `${phone.slice(0, 4)}••••${tail}`;
}

function looksLikePhone(query: string): boolean {
  return /[+\d]/.test(query) && /\d{3,}/.test(query);
}

export function CustomerLookupAutocomplete({
  onSelect,
  onCreateNew,
  placeholder = 'Search by phone or name…',
}: CustomerLookupAutocompleteProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CustomerResponse[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }

    setLoading(true);
    setError(null);
    const reqId = ++reqIdRef.current;
    const handle = setTimeout(async () => {
      try {
        const isPhone = looksLikePhone(query);
        const lookupQuery = isPhone
          ? { phone: query.trim(), pageSize: MAX_RESULTS }
          : { fullName: query.trim(), pageSize: MAX_RESULTS };
        const result = await lookupCustomers(lookupQuery);
        if (reqId !== reqIdRef.current) {
          return;
        }
        setResults(result.items.slice(0, MAX_RESULTS));
        setOpen(true);
        setLoading(false);
      } catch (err) {
        if (reqId !== reqIdRef.current) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Lookup failed');
        setResults([]);
        setOpen(true);
        setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [query]);

  const handleSelect = (customer: CustomerResponse): void => {
    onSelect(customer);
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  const handleCreate = (): void => {
    onCreateNew();
    setQuery('');
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative" data-testid="customer-lookup">
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded border border-slate-300 px-3 py-2 text-sm"
        data-testid="customer-lookup-input"
        autoComplete="off"
      />
      {open && (
        <div
          className="absolute z-10 mt-1 max-h-72 w-full overflow-y-auto rounded border border-slate-200 bg-white shadow-lg"
          data-testid="customer-lookup-results"
        >
          {loading && (
            <div className="px-3 py-2 text-sm text-slate-500">Searching…</div>
          )}
          {error !== null && (
            <div className="px-3 py-2 text-sm text-red-600">{error}</div>
          )}
          {!loading && !error && results.length === 0 && (
            <div className="px-3 py-2 text-sm text-slate-500">No matches</div>
          )}
          {results.map((customer) => (
            <button
              key={customer.id}
              type="button"
              onClick={() => handleSelect(customer)}
              data-testid={`customer-lookup-hit-${customer.id}`}
              className="flex w-full flex-col items-start gap-0.5 border-b border-slate-100 px-3 py-2 text-left text-sm hover:bg-slate-50"
            >
              <span className="font-medium text-slate-900">{customer.fullName}</span>
              <span className="text-xs text-slate-600">
                {maskPhone(customer.primaryPhone)} · {customer.customerNumber}
              </span>
            </button>
          ))}
          <button
            type="button"
            onClick={handleCreate}
            data-testid="customer-lookup-create-new"
            className="flex w-full items-center px-3 py-2 text-sm font-medium text-nipost-700 hover:bg-nipost-50"
          >
            + Create new walk-in customer
          </button>
        </div>
      )}
    </div>
  );
}

export const __test = { maskPhone, looksLikePhone };
